import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, Briefcase, Loader2, MapPin } from "lucide-react";
import { Combobox } from "../components/ui/combobox";
import { Can } from "../components/Can";
import { ACTIONS, RESOURCES } from "../constants/permissions.constants";
import { LoadingPageSkeleton } from "../components/common/LoadingPageSkeleton";
import AccessRestricted from "../components/AccessRestricted";
import { JobPosition, JobRole } from "../types";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

export default function JobPositionFormPage() {
  const { id: jobRoleId, positionId } = useParams<{ id: string; positionId?: string }>();
  const navigate = useNavigate();
  const { users, jobRoles, jobRoleLocations, fetchJobRoleLocations } = useData();
  const { currentUser } = useAuth();
  const isEditMode = Boolean(positionId) && !window.location.pathname.includes("/copy");
  const isCopyMode = window.location.pathname.includes("/copy");

  const [jobRole, setJobRole] = useState<JobRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // All positions for the "Reports To" dropdown
  const [allPositions, setAllPositions] = useState<JobPosition[]>([]);

  const [formData, setFormData] = useState({
    positionCode: "",
    location: "",
    employmentType: "Full-time",
    startDate: "",
    endDate: "",
    status: "Vacant" as "Vacant" | "Filled",
    businessJustification: "",
    reportsToType: "User" as "User" | "Position",
    reportsToUserId: "",
    reportsToPositionId: "",
    assignedUserId: ""
  });

  // Fetch next available position code when creating a new position (not edit mode)
  useEffect(() => {
    if (!isEditMode && jobRoleId) {
      const fetchNextCode = async () => {
        try {
          const response = await fetch(`${API_BASE}/api/jobroles/positions/next-code`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json"
            }
          });
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.nextCode) {
              setFormData(prev => ({ ...prev, positionCode: data.nextCode }));
            }
          }
        } catch (error) {
          console.error("Error fetching next position code:", error);
        }
      };
      fetchNextCode();
    }
  }, [isEditMode, jobRoleId]);

  useEffect(() => {
    if (jobRoleLocations.length === 0) {
      fetchJobRoleLocations();
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!jobRoleId) {
        setLoading(false);
        setHasAccess(false);
        return;
      }

      setLoading(true);
      try {
        // Fetch job role
        const roleResponse = await fetch(`${API_BASE}/api/jobroles/${jobRoleId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json"
          }
        });

        let roleData: JobRole | null = null;
        if (roleResponse.ok) {
          const data = await roleResponse.json();
          if (data.success && data.role) {
            roleData = data.role;
            setJobRole(roleData);
          }
        }

        if (!roleData) {
          setHasAccess(false);
          setLoading(false);
          return;
        }

        // Fetch all positions for reportsTo dropdown
        const positionsResponse = await fetch(`${API_BASE}/api/jobroles/positions/all`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json"
          }
        });
        if (positionsResponse.ok) {
          const posData = await positionsResponse.json();
          if (posData.success) {
            setAllPositions(posData.positions || []);
          }
        }

        if ((isEditMode || isCopyMode) && positionId) {
          // Fetch existing position
          const posResponse = await fetch(`${API_BASE}/api/jobroles/${jobRoleId}/positions`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json"
            }
          });
          if (posResponse.ok) {
            const posData = await posResponse.json();
            if (posData.success && posData.positions) {
              const position = posData.positions.find((p: JobPosition) => String(p.id) === positionId);
              if (position) {
                setFormData(prev => ({
                  ...prev,
                  positionCode: isCopyMode ? prev.positionCode : (position.positionCode || ""),
                  location: position.location || "",
                  employmentType: position.employmentType || "Full-time",
                  startDate: position.startDate ? position.startDate.split("T")[0] : "",
                  endDate: position.endDate ? position.endDate.split("T")[0] : "",
                  status: isCopyMode ? "Vacant" : (position.status || "Vacant"),
                  businessJustification: position.businessJustification || "",
                  reportsToType: position.reportsTo?.type === "Position" ? "Position" : "User",
                  reportsToUserId: position.reportsTo?.type === "User" ? String(position.reportsTo.id) : "",
                  reportsToPositionId: position.reportsTo?.type === "Position" ? String(position.reportsTo.id) : "",
                  assignedUserId: isCopyMode ? "" : (position.assignedUser?.id || "")
                }));
              }
            }
          }
        }

        setHasAccess(true);
      } catch (error) {
        console.error("Error fetching data:", error);
        setHasAccess(false);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [jobRoleId, positionId, isEditMode, isCopyMode]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.positionCode.trim()) {
      toast.error("Position code is required");
      return;
    }
    if (!formData.startDate) {
      toast.error("Start date is required");
      return;
    }
    if (formData.employmentType === "Max. Term" && !formData.endDate) {
      toast.error("End date is required for Max. Term positions");
      return;
    }

    setSubmitting(true);
    try {
      const url = isEditMode
        ? `${API_BASE}/api/jobroles/${jobRoleId}/positions/${positionId}`
        : `${API_BASE}/api/jobroles/${jobRoleId}/positions`;

      const response = await fetch(url, {
        method: isEditMode ? "PUT" : "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          positionCode: formData.positionCode,
          location: formData.location,
          employmentType: formData.employmentType,
          startDate: formData.startDate,
          endDate: formData.employmentType === "Max. Term" ? formData.endDate : null,
          status: formData.status,
          businessJustification: formData.businessJustification,
          reportsToUserId: formData.reportsToType === "User" ? (formData.reportsToUserId || undefined) : undefined,
          reportsToPositionId: formData.reportsToType === "Position" ? (formData.reportsToPositionId || undefined) : undefined,
          assignedUserId: formData.status === "Filled" ? (formData.assignedUserId || undefined) : undefined
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success(isEditMode ? "Job position updated successfully" : "Job position created successfully");
        navigate(`/job-roles/${jobRoleId}`);
      } else {
        toast.error(data.message || `Failed to ${isEditMode ? "update" : "create"} job position`);
      }
    } catch (error) {
      console.error("Error saving job position:", error);
      toast.error("An error occurred while saving the position");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/job-roles/${jobRoleId}`);
  };

  if (loading) {
    return <LoadingPageSkeleton />;
  }

  if (!hasAccess || !jobRole) {
    return <AccessRestricted backMessage="Back to Job Role" navigateTo={`/job-roles/${jobRoleId}`} accessMessage="You don't have permission to view this page." />;
  }

  const activeLocations = jobRoleLocations.filter(jl => !jl.deleted);
  const positionOptions = allPositions
    .filter(p => !isEditMode || String(p.id) !== positionId)
    .map(p => {
      const jobRoleTitle = p.jobRole?.roleName || p.jobRole?.title || "";
      const location = p.location || "";
      const isFilled = p.status === "Filled";
      const assignedUserName = p.assignedUser?.name || "";
      const statusText = isFilled ? `Filled by ${assignedUserName}` : "Vacant";
      return {
        label: `${p.positionCode} - ${jobRoleTitle} - ${location}`,
        value: String(p.id),
        subtitle: statusText
      };
    });

  const pageTitle = isEditMode ? "Edit Job Position" : "Add Job Position";
  const submitButtonText = isEditMode ? "Save Changes" : "Create Position";

  return (
    <Can I={isEditMode ? ACTIONS.UPDATE : ACTIONS.CREATE} a={RESOURCES.JOB_ROLE} passThrough={<AccessRestricted />}>
      <div className="space-y-6 p-2 md:p-0">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={handleCancel} className="flex-shrink-0">
              <ArrowLeft size={16} />
            </Button>
            <div className="flex items-center gap-2">
              <Briefcase size={20} />
              <h1 className="text-xl md:text-2xl font-bold">
                {pageTitle}
              </h1>
            </div>
          </div>
          <div className="flex gap-2 w-full md:w-auto">
            <Button variant="outline" onClick={handleCancel} className="w-full md:w-auto">
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={submitting} className="w-full md:w-auto">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {submitButtonText}
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Position Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Position Code */}
                <div className="space-y-2">
                  <Label htmlFor="positionCode">
                    Position Code <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="positionCode"
                    placeholder="e.g. JC-100"
                    value={formData.positionCode}
                    onChange={(e) => handleChange("positionCode", e.target.value)}
                    required
                  />
                </div>

                {/* Location */}
                <div className="space-y-2">
                  <Label htmlFor="location">
                    Location <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={formData.location}
                    onValueChange={(val) => handleChange("location", val)}
                  >
                    <SelectTrigger id="location">
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeLocations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.name}>
                          <div className="flex items-center gap-2">
                            <MapPin size={14} className="text-gray-400" />
                            {loc.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Employment Type */}
                <div className="space-y-2">
                  <Label htmlFor="employmentType">Employment Type</Label>
                  <Select
                    value={formData.employmentType}
                    onValueChange={(val) => handleChange("employmentType", val)}
                  >
                    <SelectTrigger id="employmentType">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Full-time">Full-time</SelectItem>
                      <SelectItem value="Part-time">Part-time</SelectItem>
                      <SelectItem value="Max. Term">Max. Term</SelectItem>
                      <SelectItem value="Casual">Casual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Start Date */}
                <div className="space-y-2">
                  <Label htmlFor="startDate">
                    Start Date <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => handleChange("startDate", e.target.value)}
                    required
                  />
                </div>

                {/* End Date (only for Max. Term) */}
                {formData.employmentType === "Max. Term" && (
                  <div className="space-y-2">
                    <Label htmlFor="endDate">
                      End Date <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="endDate"
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => handleChange("endDate", e.target.value)}
                      required
                    />
                  </div>
                )}

                {/* Status */}
                <div className="space-y-2">
                  <Label htmlFor="status">Initial Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(val) => handleChange("status", val as "Vacant" | "Filled")}
                  >
                    <SelectTrigger id="status">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Vacant">Vacant</SelectItem>
                      <SelectItem value="Filled">Filled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Assigned User (if Filled) */}
                {formData.status === "Filled" && (
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="assignedUser">Assigned User</Label>
                    <Combobox
                      options={users.filter(u => !u.archived).map(u => ({ label: u.name, value: String(u.id) }))}
                      value={formData.assignedUserId}
                      onChange={(value) => handleChange("assignedUserId", value || "")}
                      placeholder="Select user"
                      searchPlaceholder="Search user..."
                      emptyMessage="No user found."
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Reports To</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex bg-gray-100 p-1 rounded-md w-fit">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={`h-8 px-4 text-xs transition-all ${
                    formData.reportsToType === "User"
                      ? "bg-blue-600 text-white hover:bg-blue-700 hover:text-white shadow-sm"
                      : "text-gray-500 hover:bg-gray-200"
                  }`}
                  onClick={() => handleChange("reportsToType", "User")}
                >
                  User
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={`h-8 px-4 text-xs transition-all ${
                    formData.reportsToType === "Position"
                      ? "bg-blue-600 text-white hover:bg-blue-700 hover:text-white shadow-sm"
                      : "text-gray-500 hover:bg-gray-200"
                  }`}
                  onClick={() => handleChange("reportsToType", "Position")}
                >
                  Position
                </Button>
              </div>

              {formData.reportsToType === "User" ? (
                <div className="space-y-2">
                  <Label>Select User</Label>
                  <Combobox
                    options={users.filter(u => !u.archived).map(u => ({ label: u.name, value: String(u.id) }))}
                    value={formData.reportsToUserId}
                    onChange={(value) => handleChange("reportsToUserId", value || "")}
                    placeholder="Select user to report to"
                    searchPlaceholder="Search user..."
                    emptyMessage="No user found."
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Select Position</Label>
                  <Combobox
                    options={positionOptions}
                    value={formData.reportsToPositionId}
                    onChange={(value) => handleChange("reportsToPositionId", value || "")}
                    placeholder="Select position to report to"
                    searchPlaceholder="Search position code..."
                    emptyMessage="No position found."
                  />
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Additional Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="businessJustification">Business Justification</Label>
                <Textarea
                  id="businessJustification"
                  placeholder="Reasoning for this position..."
                  rows={4}
                  value={formData.businessJustification}
                  onChange={(e) => handleChange("businessJustification", e.target.value)}
                />
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </Can>
  );
}
